// src/screens/AllMatches.tsx
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { LogoLoader } from "../components/LogoLoader";
import { PlusCircle, MapPin, ArrowRight, Clock } from "lucide-react";
import i18n from "../i18n";

type Player = {
  firstName: string;
  lastName: string;
  number: number;
  position: string;
};

type Match = {
  id: string;
  opponent: string;
  date: string; // ISO, ex: "2025-08-17T18:30:00"
  location?: "domicile" | "extérieur";
  players?: Player[];
  score?: string;
  team_id?: string | null;
  user_id?: string | null;
};

type Team = {
  id: string;
  team_name: string;
  user_id?: string;
};

/* ===== i18n mini-hook (pattern CreateTeam) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const AllMatches = () => {
  const { t, lng } = useI18nSafe();

  const [matches, setMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (!user || userError) {
        navigate("/login");
        return;
      }
      const userId = user.id;

      // ---- 1) Équipes visibles (owner + membre via RLS) ----
      const { data: visibleTeams, error: teamErr } = await supabase
        .from("teams")
        .select("id, team_name, user_id")
        .or("archived.is.null,archived.eq.false");

      if (teamErr) {
        console.error("[AllMatches] teams fetch error:", teamErr);
        setTeams([]);
      } else {
        const sorted = (visibleTeams || []).sort((a, b) =>
          (a.team_name || "").localeCompare(b.team_name || "", "fr", { sensitivity: "base" })
        );
        setTeams(sorted);
        // Ne force pas de sélection ici : laissé à "toutes les équipes" par défaut
      }

      // ---- 2) Matchs liés aux équipes visibles ----
      const teamIds = (visibleTeams || []).map(t => t.id);
      let matchData: any[] | null = null;
      let matchErr: any = null;

      if (teamIds.length > 0) {
        const { data, error } = await supabase
          .from("matches")
          .select("*")
          .in("team_id", teamIds)
          .order("date", { ascending: false });
        matchData = data; matchErr = error;
      } else {
        // Fallback: si aucune équipe visible (rare), on garde le comportement historique (mes propres matchs)
        const { data, error } = await supabase
          .from("matches")
          .select("*")
          .eq("user_id", userId)
          .order("date", { ascending: false });
        matchData = data; matchErr = error;
      }

      if (matchErr) {
        console.error("[AllMatches] matches fetch error:", matchErr);
        setMatches([]);
        setLoading(false);
        return;
      }

      const parsedMatches = (matchData || []).map((match: any) => ({
        ...match,
        players:
          typeof match.players === "string"
            ? (() => { try { return JSON.parse(match.players); } catch { return []; } })()
            : Array.isArray(match.players)
              ? match.players
              : [],
      })) as Match[];

      setMatches(parsedMatches);
      setLoading(false);
    };

    fetchAll();
  }, [navigate]);

  if (loading) {
    return <LogoLoader />;
  }

  const filteredMatches = matches.filter(
    (m) => !selectedTeamId || m.team_id === selectedTeamId
  );

  // Date/heure selon langue
  const getDateTimeParts = (iso: string) => {
    const d = new Date(iso);
    const locale = lng === "fr" ? "fr-FR" : "en-US";
    const dateStr = d.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const hasTime = /T\d{2}:\d{2}/.test(iso);
    const timeStr = hasTime
      ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
      : null;
    return { dateStr, timeStr };
  };

  const locationLabel = (loc?: "domicile" | "extérieur") => {
    if (!loc) return "";
    return loc === "domicile"
      ? t("allmatches:location.home")
      : t("allmatches:location.away");
  };

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Header sticky */}
      <div
        className="sticky z-10 mb-4"
        style={{ top: "calc(env(safe-area-inset-top) + 12px)" }}
      >
        <div className="max-w-4xl mx-auto flex justify-center">
          <div className="inline-flex rounded-2xl border border-gray-200 dark:border-gray-800
                    bg-white/80 dark:bg-gray-900/80 backdrop-blur px-3 py-2">
            <button
              onClick={() => navigate("/creatematch")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded flex items-center gap-2
                   focus:outline-none focus:ring-2 focus:ring-orange-400/60"
            >
              <PlusCircle className="w-5 h-5" />
              {t("allmatches:actions.add")}
            </button>
          </div>
        </div>
      </div>

      <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-6 text-center">
        {t("allmatches:title")}
      </h1>

      <div className="max-w-4xl mx-auto w-full">
        {teams.length > 1 && (
          <div className="mb-6 flex justify-center">
            <select
              value={selectedTeamId ?? ""}
              onChange={(e) => setSelectedTeamId(e.target.value || null)}
              className="block mx-auto border rounded p-2 w-full max-w-xs
                   bg-white border-gray-300 text-gray-900
                   dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              aria-label={t("allmatches:filters.team_select_label")}
            >
              <option value="">{t("allmatches:filters.all_teams")}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.team_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {filteredMatches.length === 0 ? (
        <p className="text-center text-gray-600 dark:text-gray-400">
          {t("allmatches:empty")}
        </p>
      ) : (
        <div className="space-y-6 max-w-4xl mx-auto">
          {filteredMatches.map((match) => {
            const { dateStr, timeStr } = getDateTimeParts(match.date);
            return (
              <div
                key={match.id}
                className="bg-white border border-gray-200 p-4 rounded-2xl shadow-sm
                           dark:bg-gray-900 dark:border-gray-800"
              >
                <div className="mb-2 flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {t("allmatches:versus")} {match.opponent}
                    {match.score && (
                      <span className="text-sm text-gray-600 dark:text-gray-400 ml-2 font-normal">
                        | {t("allmatches:score")} {match.score}
                      </span>
                    )}
                  </h2>

                  {/* Date + Time */}
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 sm:mt-0 flex items-center gap-3">
                    <span>{dateStr}</span>
                    {timeStr && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {timeStr}
                      </span>
                    )}
                  </div>
                </div>

                {match.location && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-2 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {t("allmatches:location.label")} : {locationLabel(match.location)}
                  </p>
                )}

                <div className="text-sm text-gray-800 dark:text-gray-200">
                  <p className="font-medium mb-1">
                    {new Date(match.date).getTime() > Date.now()
                      ? t("allmatches:players.title_upcoming")
                      : t("allmatches:players.title")}
                  </p>
                  {match.players && match.players.length > 0 ? (
                    <ul className="list-disc pl-5 mb-2">
                      {match.players.map((player, index) => (
                        <li key={index}>
                          {player.number} - {player.firstName} {player.lastName} ({player.position})
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600 dark:text-gray-400 italic mb-2">
                      {t("allmatches:players.none")}
                    </p>
                  )}

                  <Link
                    to={`/matchdetails/${match.id}`}
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline text-sm"
                  >
                    {t("allmatches:view_stats")}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
